import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface UseTabStateTab {
  key: string;
  content: ReactNode;
  /**
   * URL search-param name to sync the active tab against. If any tab in the
   * set defines this, the first defined value wins and the param drives the
   * active tab. If no tab defines it, the hook owns tab state internally.
   */
  queryParam?: string;
}

/**
 * Owns "which tab is active" state for a `RedoHorizontalTabs` consumer.
 *
 * If any tab declares a `queryParam`, that URL search param drives the
 * active tab (`?<param>=<tab.key>`); switching tabs writes to the URL.
 * Otherwise the hook keeps the active tab in internal `useState`.
 *
 * Stale URL values (a key that no longer exists in `tabs`) fall back to the
 * first tab.
 */
export function useTabState(tabs: UseTabStateTab[] | undefined): {
  selectedKey: string | undefined;
  setSelectedKey: (key: string) => void;
  activeContent: ReactNode | undefined;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabQueryParamKey = useMemo(
    () => tabs?.find((t) => t.queryParam !== undefined)?.queryParam,
    [tabs],
  );
  const firstKey = tabs?.[0]?.key;

  const [internalKey, setInternalKey] = useState<string | undefined>(firstKey);

  const urlValue =
    tabQueryParamKey !== undefined ? searchParams.get(tabQueryParamKey) : null;

  const candidateKey =
    tabQueryParamKey !== undefined ? (urlValue ?? firstKey) : internalKey;

  const selectedKey =
    candidateKey !== undefined && tabs?.some((t) => t.key === candidateKey)
      ? candidateKey
      : firstKey;

  const setSelectedKey = useCallback(
    (key: string) => {
      if (tabQueryParamKey !== undefined) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(tabQueryParamKey, key);
            return next;
          },
          { replace: false },
        );
      } else {
        setInternalKey(key);
      }
    },
    [tabQueryParamKey, setSearchParams],
  );

  const activeContent = useMemo(
    () => tabs?.find((t) => t.key === selectedKey)?.content,
    [tabs, selectedKey],
  );

  // If the URL points at a tab that no longer exists, replace it with the
  // resolved key so the address bar matches what's rendered (otherwise
  // bookmarks, share links, and back-nav read a ghost key).
  useEffect(() => {
    if (tabQueryParamKey === undefined) {
      return;
    }
    if (
      urlValue !== null &&
      urlValue !== selectedKey &&
      selectedKey !== undefined
    ) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(tabQueryParamKey, selectedKey);
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQueryParamKey, urlValue, selectedKey, setSearchParams]);

  return { selectedKey, setSelectedKey, activeContent };
}
